import { ShieldCheck, Wrench } from "lucide-react";
import Booking from "./Booking";
import ServicePageFaqSection from "./ServicePageFaqSection";
import { useFaqPageJsonLd } from "@/hooks/useFaqPageJsonLd";

const WORK_GALLERY_IMAGES = [
  "/resources/pilkington-work-1.png",
  "/resources/pilkington-work-2.png",
  "/resources/pilkington-work-3.png",
  "/resources/pilkington-work-4.png",
  "/resources/pilkington-work-5.png",
  "/resources/pilkington-work-6.png",
];

export default function PilkingtonPage({ t }) {
  useFaqPageJsonLd(t.pilkingtonPage.faq, "service-pilkington-faq");

  return (
    <article
      id="pilkington-page"
      data-testid="pilkington-page"
      className="bg-white border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <header>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#e3191b] mb-4">
            {t.pilkingtonPage.eyebrow}
          </div>
          <h1 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            {t.pilkingtonPage.title}
          </h1>
          <p className="mt-6 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
            {t.pilkingtonPage.lead}
          </p>
          {t.pilkingtonPage.secondary ? (
            <p className="mt-5 max-w-3xl text-slate-600 leading-relaxed text-base sm:text-lg">
              {t.pilkingtonPage.secondary}
            </p>
          ) : null}
        </header>

        <div className="mt-8 border border-[#e2e6ec] bg-white overflow-hidden">
          <img
            src="/resources/pilkington-work-hero.png"
            alt={t.pilkingtonPage.heroImageAlt}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>

        <div className="mt-10 border border-[#e2e6ec] bg-[#f7f8fa]">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            <div className="lg:col-span-5 bg-white border-b lg:border-b-0 lg:border-r border-[#e2e6ec] p-5 lg:p-8 flex items-center justify-center">
              <img
                src="/resources/pilkington-team-partner.png"
                alt={t.pilkingtonPage.imageAlt}
                className="w-full max-w-[460px] h-auto object-contain"
                loading="lazy"
              />
            </div>
            <div className="lg:col-span-7 p-6 lg:p-8">
              <h2 className="font-[Barlow_Condensed] font-bold uppercase text-[#0b1220] text-3xl tracking-tight">
                {t.pilkingtonPage.subtitle}
              </h2>
              <ul className="mt-5 space-y-3">
                {t.pilkingtonPage.bullets.map((item, i) => (
                  <li key={i} className="text-slate-700 leading-relaxed flex items-start gap-2">
                    <span className="text-[#0b378c] mt-1" aria-hidden="true">
                      •
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <section className="mt-10" aria-labelledby="pilkington-cards-heading">
          <h2
            id="pilkington-cards-heading"
            className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-2xl sm:text-3xl mb-8 max-w-4xl leading-tight"
          >
            {t.pilkingtonPage.cardsSectionTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <ShieldCheck className="w-4 h-4" />
                {t.pilkingtonPage.card1Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.pilkingtonPage.card1Text}</p>
            </div>
            <div className="border border-[#e2e6ec] bg-white p-6">
              <div className="inline-flex items-center gap-2 text-[#0b378c] font-mono text-xs uppercase tracking-[0.25em] mb-3">
                <Wrench className="w-4 h-4" />
                {t.pilkingtonPage.card2Title}
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{t.pilkingtonPage.card2Text}</p>
            </div>
          </div>
        </section>

        <div className="mt-6 border border-[#e2e6ec] bg-[#f7f8fa] p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#0b378c] mb-1">
            {t.pilkingtonPage.chipRepairEyebrow}
          </div>
          <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">{t.pilkingtonPage.chipRepairText}</p>
          <div className="mt-3 border border-[#e2e6ec] bg-white p-2">
            <img
              src={t.pilkingtonPage.chipRepairImage}
              alt={t.pilkingtonPage.chipRepairImageAlt}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#0b378c] mb-3">
            {t.pilkingtonPage.workGalleryEyebrow}
          </div>
          <h2 className="font-[Barlow_Condensed] font-bold uppercase tracking-tight text-[#0b1220] text-3xl">
            {t.pilkingtonPage.workGalleryTitle}
          </h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WORK_GALLERY_IMAGES.map((src, i) => (
              <div key={src} className="border border-[#e2e6ec] bg-white overflow-hidden">
                <img
                  src={src}
                  alt={`${t.pilkingtonPage.workGalleryImageAlt} ${i + 1}`}
                  className="w-full h-[260px] object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <ServicePageFaqSection
          headingId="pilkington-faq-heading"
          title={t.pilkingtonPage.faqTitle}
          faq={t.pilkingtonPage.faq}
        />

        <div className="mt-12">
          <Booking
            t={t}
            initialService={t.services.items[3]?.title || ""}
            copyOverrides={t.pilkingtonPage.bookingCopy}
          />
        </div>
      </div>
    </article>
  );
}
